<template>
  <el-dialog
    title="颜色配置"
    :visible.sync="dialogVisible"
    width="520px"
    append-to-body
    :close-on-click-modal="false"
    custom-class="trajectory-color-config-dialog"
    @open="loadDraft"
    @closed="$emit('closed')"
  >
    <section class="tcc-section">
      <h4 class="tcc-section__title">轨迹颜色</h4>
      <el-select v-model="draft.selectValue" class="tcc-color-select" placeholder="请选择">
        <el-option
          v-for="opt in solidColorOptions"
          :key="opt.value"
          :label="opt.label"
          :value="opt.value"
        >
          <div class="tcc-color-option">
            <span class="tcc-color-option__swatch" :style="{ backgroundColor: opt.swatch }"></span>
            <span>{{ opt.label }}</span>
          </div>
        </el-option>
        <el-option :label="speedModeLabel" :value="speedModeValue" />
      </el-select>
    </section>

    <section v-if="isSpeedMode" class="tcc-section">
      <h4 class="tcc-section__title">按速度配置</h4>
      <div class="tcc-color-bar">
        <div class="tcc-color-bar__track" :style="colorTrackStyle"></div>
        <el-slider
          v-model="draft.speedNodes"
          range
          :min="1"
          :max="119"
          :step="1"
          :show-tooltip="true"
          tooltip-class="tcc-slider-tooltip"
        />
      </div>
      <div class="tcc-axis">
        <span v-for="tick in axisTicks" :key="tick">{{ tick }}</span>
      </div>
      <p class="tcc-tip">拖拽控制柄设置速度节点 (km/h)</p>
    </section>

    <span slot="footer" class="dialog-footer">
      <el-button @click="dialogVisible = false">取消</el-button>
      <el-button type="primary" @click="confirm">确定</el-button>
    </span>
  </el-dialog>
</template>

<script>
import {
  DEFAULT_SPEED_CURVE_SETTINGS,
  SPEED_COLOR_STOPS,
  SPEED_SLIDER_MAX,
  getSpeedCurveSettings,
  normalizeSpeedCurveSettings,
  saveSpeedCurveSettings
} from '@/utils/speedCurveSettings'
import {
  TRAJECTORY_COLOR_MODE_SOLID,
  TRAJECTORY_COLOR_MODE_SPEED,
  TRAJECTORY_SOLID_COLOR_OPTIONS,
  TRAJECTORY_SPEED_MODE_VALUE,
  getTrajectoryColorSettings,
  saveTrajectoryColorSettings
} from '@/utils/trajectoryColorSettings'

export default {
  name: 'TrajectoryColorConfigDialog',
  props: {
    visible: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      solidColorOptions: TRAJECTORY_SOLID_COLOR_OPTIONS,
      speedModeValue: TRAJECTORY_SPEED_MODE_VALUE,
      speedModeLabel: '按速度配置',
      draft: {
        selectValue: TRAJECTORY_SOLID_COLOR_OPTIONS[2].value,
        speedNodes: [...DEFAULT_SPEED_CURVE_SETTINGS.speedNodes]
      },
      axisTicks: [0, 20, 40, 60, 80, 100, SPEED_SLIDER_MAX]
    }
  },
  computed: {
    dialogVisible: {
      get() {
        return this.visible
      },
      set(val) {
        this.$emit('update:visible', val)
      }
    },
    isSpeedMode() {
      return this.draft.selectValue === this.speedModeValue
    },
    colorTrackStyle() {
      const nodes = normalizeSpeedCurveSettings({ speedNodes: this.draft.speedNodes }).speedNodes
      const p1 = (nodes[0] / SPEED_SLIDER_MAX) * 100
      const p2 = (nodes[1] / SPEED_SLIDER_MAX) * 100
      return {
        background: `linear-gradient(to right,
          ${SPEED_COLOR_STOPS[0]} 0%,
          ${SPEED_COLOR_STOPS[0]} ${p1}%,
          ${SPEED_COLOR_STOPS[1]} ${p1}%,
          ${SPEED_COLOR_STOPS[1]} ${p2}%,
          ${SPEED_COLOR_STOPS[2]} ${p2}%,
          ${SPEED_COLOR_STOPS[2]} 100%)`
      }
    }
  },
  methods: {
    loadDraft() {
      const colorCfg = getTrajectoryColorSettings()
      const speedCfg = getSpeedCurveSettings()
      this.draft = {
        selectValue:
          colorCfg.mode === TRAJECTORY_COLOR_MODE_SPEED
            ? this.speedModeValue
            : colorCfg.solidColor,
        speedNodes: [...speedCfg.speedNodes]
      }
    },
    confirm() {
      const isSpeed = this.isSpeedMode
      const solidColor = isSpeed
        ? getTrajectoryColorSettings().solidColor
        : this.draft.selectValue
      const colorSettings = saveTrajectoryColorSettings({
        mode: isSpeed ? TRAJECTORY_COLOR_MODE_SPEED : TRAJECTORY_COLOR_MODE_SOLID,
        solidColor
      })
      let speedSettings = null
      if (isSpeed) {
        speedSettings = saveSpeedCurveSettings({
          ...getSpeedCurveSettings(),
          speedNodes: this.draft.speedNodes
        })
      }
      this.dialogVisible = false
      this.$emit('saved', { ...colorSettings, speedSettings })
    }
  }
}
</script>

<style scoped>
.tcc-section + .tcc-section {
  margin-top: 1.25rem;
}

.tcc-section__title {
  margin: 0 0 0.75rem;
  padding-left: 0.5rem;
  border-left: 0.1875rem solid var(--layout-primary, #087dff);
  font-size: 0.875rem;
  font-weight: 600;
  color: #303133;
  line-height: 1.2;
}

.tcc-color-select {
  width: 100%;
}

.tcc-color-option {
  display: flex;
  align-items: center;
  gap: 0.375rem;
}

.tcc-color-option__swatch {
  width: 0.625rem;
  height: 0.625rem;
  border-radius: 0.125rem;
}

.tcc-color-bar {
  position: relative;
  padding: 0 0.625rem;
}

.tcc-color-bar__track {
  position: absolute;
  left: 0.625rem;
  right: 0.625rem;
  top: 50%;
  height: 0.375rem;
  margin-top: -0.1875rem;
  border-radius: 0.1875rem;
  pointer-events: none;
}

.tcc-color-bar >>> .el-slider__runway {
  background: transparent;
}

.tcc-color-bar >>> .el-slider__bar {
  background: transparent;
}

.tcc-axis {
  display: flex;
  justify-content: space-between;
  margin: 0.375rem 0.625rem 0;
  font-size: 0.75rem;
  color: #909399;
}

.tcc-tip {
  margin: 0.5rem 0 0;
  font-size: 0.75rem;
  color: #909399;
}
</style>
